import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

/**
 * ErrorBoundary - Catches JavaScript errors in child components
 * 
 * Prevents the entire app from crashing when a component throws an error.
 * Automatically recovers from ChunkLoadErrors (stale cache after updates)
 * by reloading the page once. Shows a user-friendly error for other errors.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });

    // Auto-recover from ChunkLoadErrors (stale cache after code updates)
    // Only auto-reload once to prevent infinite reload loops
    const isChunkError = error.name === 'ChunkLoadError' || 
      error.message?.includes('Loading chunk') ||
      error.message?.includes('Unexpected token');
    
    if (isChunkError) {
      const lastReload = sessionStorage.getItem('chunk_error_reload');
      const now = Date.now();
      
      // Check if there's unsaved work — don't auto-reload if so
      const hasUnsavedWork = sessionStorage.getItem('has_unsaved_work') === 'true';
      
      // Only auto-reload if no unsaved work and haven't reloaded in the last 30 seconds
      if (!hasUnsavedWork && (!lastReload || (now - parseInt(lastReload)) > 30000)) {
        console.warn('[ErrorBoundary] ChunkLoadError detected — clearing caches and reloading');
        sessionStorage.setItem('chunk_error_reload', now.toString());
        
        // Clear the service worker runtime cache to remove stale chunks
        if ('caches' in window) {
          caches.keys().then(keys => {
            Promise.all(
              keys.filter(k => k.includes('runtime') || k.includes('fmiq')).map(k => caches.delete(k))
            ).then(() => {
              window.location.reload();
            });
          });
        } else {
          window.location.reload();
        }
        return;
      }
    }
    
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isChunkError = this.state.error?.name === 'ChunkLoadError' || 
        this.state.error?.message?.includes('Loading chunk') ||
        this.state.error?.message?.includes('Unexpected token');

      // Default error UI
      return (
        <div className="min-h-[400px] flex items-center justify-center p-4">
          <Card className="max-w-md w-full">
            <CardContent className="pt-6">
              <div className="text-center">
                <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                  {isChunkError ? 'Loading Issue' : 'Something went wrong'}
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                  {isChunkError 
                    ? 'The app needs to refresh to load updated files. This can happen after an update or when connectivity is unstable.'
                    : (this.props.message || 'The application encountered an error. Please try reloading the page.')}
                </p>
                
                {this.state.error && (
                  <details className="text-left text-xs bg-slate-100 dark:bg-slate-800 p-3 rounded mb-4 max-h-32 overflow-auto">
                    <summary className="cursor-pointer text-slate-600 dark:text-slate-300 mb-2">
                      Error Details
                    </summary>
                    <pre className="text-red-600 dark:text-red-400 whitespace-pre-wrap">
                      {this.state.error.toString()}
                    </pre>
                  </details>
                )}
                
                <div className="flex gap-2 justify-center">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      // Clear caches before reloading
                      if ('caches' in window) {
                        caches.keys().then(keys => {
                          Promise.all(keys.map(k => caches.delete(k))).then(() => {
                            window.location.reload();
                          });
                        });
                      } else {
                        window.location.reload();
                      }
                    }}
                    className="gap-2"
                    data-testid="error-boundary-reload-btn"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Reload Page
                  </Button>
                  {this.props.onRetry && !isChunkError && (
                    <Button 
                      onClick={() => {
                        this.handleRetry();
                        this.props.onRetry?.();
                      }}
                      className="bg-teal-600 hover:bg-teal-700"
                    >
                      Try Again
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
